import { memo, type SyntheticEvent, useCallback, useState } from 'react';
import { useDebouncedCallback } from '@mantine/hooks';
import {
  Autocomplete,
  Chip,
  FormControl,
  TextField,
  type AutocompleteChangeReason,
} from '@mui/material';
import { useDynamicFieldContext } from '../DynamicFieldContext';
import { DenumFieldProps } from './DenumField';
import { CoreFieldEnumOpt } from 'providers/contexts/extensions/DynamicFieldData';

type TDomainFieldProps = DenumFieldProps;

export const DomainField = memo(
  ({ fieldSx, value, onChange, onValidationChange }: TDomainFieldProps) => {
    const { fieldConfig, searchText, updateSearchText, dynamicOptions } =
      useDynamicFieldContext();

    const [validationError, setValidationError] = useState<string>('');

    // Multiple is the fallback.
    const isMultiple = fieldConfig.config?.multiple ?? true;

    const normalizeDomainValue = (val: any) => {
      if (isMultiple) {
        return Array.isArray(val) ? val : val ? [val] : [];
      }
      return val || '';
    };

    const validateValue = useCallback(
      (val: string | string[] | null) => {
        // Check if field is required
        if (fieldConfig.required) {
          const isValid = isMultiple
            ? Array.isArray(val) && val.length > 0
            : typeof val === 'string' && val.length > 0;

          if (!isValid) {
            const errorMsg = `${fieldConfig.label || fieldConfig.name} is required`;
            setValidationError(errorMsg);
            onValidationChange?.(fieldConfig.name, false);
            return false;
          }
        }

        // If validation passes, clear error.
        setValidationError('');
        onValidationChange?.(fieldConfig.name, true);
        return true;
      },
      [
        fieldConfig.required,
        fieldConfig.name,
        fieldConfig.label,
        isMultiple,
        onValidationChange,
      ],
    );

    const debouncedSearchUpdate = useDebouncedCallback((searchValue: string) => {
      updateSearchText(searchValue);
    }, 300);

    const handleOnChange = (
      event: SyntheticEvent,
      newValue: string | string[] | null,
      reason: AutocompleteChangeReason,
    ) => {
      const finalValue = newValue ?? (isMultiple ? [] : '');

      // Validate the new value
      validateValue(finalValue);

      // Update the form value.
      onChange(fieldConfig.name, finalValue);

      // Clear search text on selection so that all options are shown again.
      updateSearchText('');
    };

    return (
      <FormControl
        data-testid="domain-field-control"
        fullWidth
        variant="outlined"
        sx={{
          my: 0,
          ...fieldSx,
        }}
      >
        <Autocomplete
          data-testid="domain-field-autocomplete"
          multiple={isMultiple}
          freeSolo // for allowing search text that doesn't match options.
          value={normalizeDomainValue(value)}
          getOptionLabel={(option) => {
            if (typeof option === 'string') return option;
            if (Array.isArray(option)) return option.join(', ');
            return option?.label ?? option?.value ?? '';
          }}
          isOptionEqualToValue={(option, val) => {
            if (typeof option === 'string' && typeof val === 'string') {
              return option === val;
            }
            if (
              typeof option === 'object' &&
              option !== null &&
              typeof val === 'object' &&
              val !== null
            ) {
              return option.value === (val as any).value;
            }
            return false;
          }}
          onChange={handleOnChange}
          onBlur={() => {
            validateValue(normalizeDomainValue(value));
          }}
          onInputChange={(_, newInputValue, reason) => {
            // Only update search text when user is typing, not when clearing/resetting
            if (reason === 'input') {
              debouncedSearchUpdate(newInputValue);
            }
          }}
          options={
            dynamicOptions.length > 0
              ? dynamicOptions.map((option) => option.value)
              : (fieldConfig.config?.options?.map(
                  (option: CoreFieldEnumOpt) => option.value,
                ) ?? [])
          }
          renderValue={(value, getItemProps) => {
            return value?.map((option: string | number | any, index: number) => {
              const itemProps = { ...getItemProps({ index }) } as Record<string, any>;

              // MUI includes key in props object; remove before JSX spread to avoid React warning.
              delete itemProps.key;

              return (
                <Chip
                  data-testid={`domain-field-chip--${option}`}
                  key={`${option}-${index}`}
                  label={option}
                  size="small"
                  {...itemProps}
                />
              );
            });
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              data-testid="domain-field-input"
              variant="outlined"
              value={searchText || ((params as any)?.inputProps?.value ?? '')}
              error={!!validationError}
              helperText={validationError}
              sx={{
                my: 0,
                '& .MuiInputBase-root': {
                  mt: fieldConfig?.config?.hideLabel ? 0 : undefined,
                  pt: 0,
                },
              }}
            />
          )}
        />
      </FormControl>
    );
  },
);
DomainField.displayName = 'DomainField';
